import React from 'react';
import { Helmet } from 'react-helmet-async';

const Privacy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Personvernerklæring - Advokatfirmaet Bruland</title>
        <meta
          name="description"
          content="Personvernerklæring for Advokatfirmaet Bruland. Les om hvordan vi behandler personopplysninger."
        />
      </Helmet>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8" style={{ color: '#1a365d' }}>
          Personvernerklæring
        </h1>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              1. Innledning
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Advokatfirmaet Bruland AS respekterer ditt personvern og er opptatt av å beskytte dine personopplysninger.
              Denne personvernerklæringen beskriver hvordan vi samler inn, bruker og beskytter opplysninger når du besøker
              vår nettside bruland.no.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              2. Behandlingsansvarlig
            </h2>
            <p className="text-gray-700 leading-relaxed">
              <strong>Advokatfirmaet Bruland Farstad AS</strong><br />
              Organisasjonsnummer: 826867302<br />
              Adresse: Drammensveien 82c, 0271 Oslo<br />
              E-post: advokat@bruland.no<br />
              Telefon: +47 22 53 53 22
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              3. Hvilke opplysninger samler vi inn?
            </h2>

            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#1a365d' }}>
              3.1 Cookies og Google Analytics
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Vi bruker Google Analytics for å analysere trafikk på nettstedet vårt. Dette innebærer bruk av cookies
              (informasjonskapsler) som samler inn anonymiserte data om:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Hvilke sider du besøker</li>
              <li>Hvor lenge du er på hver side</li>
              <li>Hvordan du kom til nettstedet vårt</li>
              <li>Din nettleser og enhet</li>
              <li>Din omtrentlige geografiske plassering (by/region)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Vi samler <strong>ikke</strong> inn personlig identifiserbar informasjon gjennom Google Analytics,
              og IP-adresser anonymiseres.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#1a365d' }}>
              3.2 Kontaktskjema
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Når du sender oss en henvendelse via kontaktskjemaet, samler vi inn:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Navn</li>
              <li>E-postadresse</li>
              <li>Telefonnummer (valgfritt)</li>
              <li>Melding/beskrivelse av saken</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              4. Formål med behandlingen
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Vi behandler personopplysninger for følgende formål:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Google Analytics:</strong> For å forbedre nettstedet og brukeropplevelsen</li>
              <li><strong>Kontaktskjema:</strong> For å besvare henvendelser og vurdere saken din</li>
              <li><strong>Klientoppfølging:</strong> For å yte juridiske tjenester (ved oppstart av klientforhold)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              5. Rettslig grunnlag
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Vårt rettslige grunnlag for behandling av personopplysninger er:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Samtykke (GDPR art. 6.1.a):</strong> Ved bruk av cookies/Google Analytics</li>
              <li><strong>Berettiget interesse (GDPR art. 6.1.f):</strong> For å besvare henvendelser</li>
              <li><strong>Kontraktsoppfyllelse (GDPR art. 6.1.b):</strong> Ved oppstart av klientforhold</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              6. Deling av opplysninger
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Vi deler ikke dine personopplysninger med tredjeparter, med unntak av:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Google Analytics:</strong> Anonymiserte data behandles av Google (se Google sin personvernerklæring)</li>
              <li><strong>Lovpålagte utleveringer:</strong> Når vi er forpliktet av lov til å utlevere informasjon</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              7. Lagringstid
            </h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Google Analytics data:</strong> Automatisk slettet etter 26 måneder</li>
              <li><strong>Cookies:</strong> Utløper automatisk eller ved å slette cookies i nettleseren din</li>
              <li><strong>Henvendelser:</strong> Slettes når de ikke lenger er nødvendige</li>
              <li><strong>Klientsaker:</strong> Oppbevares i henhold til advokatlovens regler</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              8. Dine rettigheter
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Du har følgende rettigheter i henhold til GDPR:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Rett til innsyn:</strong> Du kan be om en kopi av dine personopplysninger</li>
              <li><strong>Rett til retting:</strong> Du kan be om at uriktige opplysninger rettes</li>
              <li><strong>Rett til sletting:</strong> Du kan be om at opplysninger slettes</li>
              <li><strong>Rett til begrensning:</strong> Du kan be om at behandling av opplysninger begrenses</li>
              <li><strong>Rett til dataportabilitet:</strong> Du kan be om å få dine data i et strukturert format</li>
              <li><strong>Rett til å protestere:</strong> Du kan protestere mot behandling av dine opplysninger</li>
              <li><strong>Rett til å trekke samtykke:</strong> Du kan når som helst trekke tilbake ditt samtykke</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              For å utøve dine rettigheter, kontakt oss på <a href="mailto:advokat@bruland.no" style={{ color: '#b8860b', textDecoration: 'underline' }}>advokat@bruland.no</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              9. Cookies og hvordan du styrer dem
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Du kan når som helst:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Slette cookies i nettleserens innstillinger</li>
              <li>Blokkere cookies i nettleserens innstillinger</li>
              <li>Endre ditt samtykke ved å slette cookies og oppdatere siden</li>
              <li>Bruke Google Analytics opt-out: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{ color: '#b8860b', textDecoration: 'underline' }}>Google Analytics Opt-out</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              10. Sikkerhet
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Vi tar informasjonssikkerhet på alvor og har implementert tekniske og organisatoriske sikkerhetstiltak
              for å beskytte dine personopplysninger mot uautorisert tilgang, endring, avsløring eller ødeleggelse.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              11. Klagerett
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Du har rett til å klage til Datatilsynet dersom du mener at vår behandling av personopplysninger
              er i strid med personvernregelverket:
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Datatilsynet</strong><br />
              Postboks 458 Sentrum, 0105 Oslo<br />
              Telefon: 22 39 69 00<br />
              E-post: postkasse@datatilsynet.no<br />
              Nettside: <a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer" style={{ color: '#b8860b', textDecoration: 'underline' }}>www.datatilsynet.no</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              12. Endringer i personvernerklæringen
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Vi kan oppdatere denne personvernerklæringen fra tid til annen. Siste versjon vil alltid være tilgjengelig
              på denne siden.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Sist oppdatert:</strong> {new Date().toLocaleDateString('nb-NO', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </section>

          <section className="mt-12 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: '#1a365d' }}>
              Kontakt oss
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Har du spørsmål om hvordan vi behandler personopplysninger, eller vil du utøve dine rettigheter?<br />
              Ta kontakt med oss:
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>E-post:</strong> <a href="mailto:advokat@bruland.no" style={{ color: '#b8860b', textDecoration: 'underline' }}>advokat@bruland.no</a><br />
              <strong>Telefon:</strong> +47 22 53 53 22<br />
              <strong>Besøksadresse:</strong> Drammensveien 82c, 0271 Oslo
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default Privacy;
